import { ChangeDetectorRef, Component, EventEmitter, OnInit, Input, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { finalize, takeUntil } from 'rxjs/operators';
import { DataService } from 'src/app/services/data.service';
import { UserUtilityService } from 'src/app/services/user-utility.service';
import { IceapiService } from '../../../../../services/index';
import { ManufacturerModel } from '../../../models/index';
import { Subject } from 'rxjs';
import { JointCalcService } from 'src/app/Modules/idms/components/plant-manager/services/joint-calc-service.service';
import { Router } from '@angular/router';

@Component({
    selector: 'app-clamp-manufacturer',
    templateUrl: './clamp-manufacturer.component.html',
    styleUrls: ['./clamp-manufacturer.component.scss'],
    standalone: false,
})
export class ClampManufacturerComponent implements OnInit {
    @Input() form: UntypedFormGroup;
    @Input() enable: boolean;
    @Output() selected: EventEmitter<ManufacturerModel> = new EventEmitter();
    @Output() misalignmentFactorCheck: EventEmitter<boolean> = new EventEmitter();

    tooltip_position = 'above';
    loading: boolean = false;
    misalignmentFactor: boolean = false;
    destroy$ = new Subject();
    optionList: ManufacturerModel[];

    constructor(
        private iceapi: IceapiService,
        private dataService: DataService,
        private user_utility: UserUtilityService,
        private cd: ChangeDetectorRef,
        private router: Router,
        private jointCalcService: JointCalcService
    ) {}

    ngOnInit() {
        if (!this.jointCalcService.isPipeCode) {
            this.getClampManufacturers();
        } else if ((this.router.url == '/idms/plantmanager' || this.router.url.includes('/idms/plantmanagerView')) && this.jointCalcService.isPipeCode) {
            this.pipespecwithclampManufacturer();
        }

        this.form
            .get('clampManufacturer')
            .valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe((value) => {
                if (value)
                    if ((this.router.url == '/idms/plantmanager' || this.router.url.includes('/idms/plantmanagerView')) && this.jointCalcService.isPipeCode)
                        this.jointCalcService.selectedClampManufacturer.next(value);
            });
    }

    getClampManufacturers(): void {
        this.form.markAllAsTouched();
        this.loading = true;
        this.iceapi
            .getManufacturers(1)
            .pipe(
                finalize(() => {
                    this.loading = false;
                })
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe((manufacturerModels) => {
                if (manufacturerModels.success === true) {
                    this.optionList = manufacturerModels.response;
                    this.cd.markForCheck();
                    if (this.form.get('clampManufacturer').value) {
                        this.dataService.selectedClampManufacturer = this.optionList.find(
                            (x) => x.manufacturerId === this.form.get('clampManufacturer').value
                        ).name;
                    }
                    if (this.optionList.length === 1) this.form.get('clampManufacturer').setValue(this.optionList[0].manufacturerId);
                    if (this.form.get('clampManufacturer').value != null) {
                        let clampManufacturer = this.optionList.find((x) => x.manufacturerId === this.form.get('clampManufacturer').value);
                        //Check the permitsClampAlignment, to display the misalignment factor field
                        this.misalignmentFactorCheck.emit(clampManufacturer.permitsClampAlignment);
                    }
                } else {
                    this.user_utility.errorModelPopup(manufacturerModels.message);
                }
            });
    }

    selectClampManufacturer(selectedValue: MatSelectChange): void {
        const selection = this.optionList.find((x) => x.manufacturerId === selectedValue.value);
        //Check the permitsClampAlignment, to display the misalignment factor field
        this.misalignmentFactorCheck.emit(selection.permitsClampAlignment);

        if (selection) {
            this.selected.emit(selection);
        }
        this.dataService.selectedClampManufacturer = selection.name;
    }

    async pipespecwithclampManufacturer() {
        this.jointCalcService.selectedLubricant.pipe(takeUntil(this.destroy$)).subscribe(async (lub) => {
            if (lub) {
                this.loading = true;
                const { minNominalPipeSize, maxNominalPipeSize } = this.getMinMaxPipeSizes([...this.jointCalcService.pipeSpecList]);

                let pipeSpecClampManufacturer = await this.iceapi
                    .getManufacturers(1, minNominalPipeSize, maxNominalPipeSize)
                    .pipe(
                        finalize(() => {
                            this.loading = false;
                            this.cd.detectChanges();
                        })
                    )
                    .toPromise();

                if (pipeSpecClampManufacturer.success) {
                    this.optionList = pipeSpecClampManufacturer.response;
                    const inputGroup = this.jointCalcService.pipeSpecpayload.lookupParams;
                    inputGroup.recordId.lubricantId = lub;

                    inputGroup.recordId.clampId =
                        inputGroup.recordId.pipeSizeId =
                        inputGroup.recordId.boltMaterialId =
                        inputGroup.recordId.clampMaterialId =
                        inputGroup.recordId.pipeScheduleId =
                        inputGroup.recordId.hubMaterialId =
                        inputGroup.recordId.sealRingMaterialId =
                        inputGroup.recordId.sealRingId =
                        inputGroup.others.boltType =
                            null;
                    this.optionList = await this.jointCalcService.managePayloadForRecourdId('manufacturerId', this.optionList);

                    if (this.dataService?.calculationData?.input?.recordId?.manufacturerId)
                        this.form.get('clampManufacturer').setValue(this.dataService?.calculationData?.input?.recordId?.manufacturerId);
                    else if (this.optionList.length === 1) this.form.get('clampManufacturer').setValue(this.optionList[0].manufacturerId);
                } else if (pipeSpecClampManufacturer.isCustom) {
                    this.user_utility.errorModelPopup(pipeSpecClampManufacturer.message);
                }
            }
        });
    }

    getMinMaxPipeSizes(pipeSpecList: any[]): { minNominalPipeSize: number; maxNominalPipeSize: number } {
        // Extract all minimumNominalPipeSize and maximumNominalPipeSize values
        const minimumPipeSizes = pipeSpecList.map((item) => item.minimumNominalPipeSize);
        const maximumPipeSizes = pipeSpecList.map((item) => item.maximumNominalPipeSize);

        // Find the smallest minimumNominalPipeSize and the largest maximumNominalPipeSize
        const minNominalPipeSize = Math.min(...minimumPipeSizes);
        const maxNominalPipeSize = Math.max(...maximumPipeSizes);

        return { minNominalPipeSize, maxNominalPipeSize };
    }

    // Usage example

    ngOnDestroy(): void {
        this.destroy$.next(null);
        this.destroy$.complete();
    }
}
